// Lettura/scrittura di valori nello scope di un flow tramite dot notation.
// Isolato in un file suo perché serve sia a `conditions.ts` sia a `template.ts`
// (metterlo in uno dei due creerebbe un import circolare).

import { isRef, unref } from 'vue'
import type { FlowScope } from './flowTypes'

/** Root riconosciute in testa a un path. Tutto il resto ricade su `item`. */
const KNOWN_ROOTS = new Set([
  'item', 'answers', 'vars', 'inputs', 'original', 'changes', 'user', 'setting', 'env', '$answer',
  // Presente solo mentre si valuta `optionsWhere`: è l'opzione sotto esame.
  'option',
])

/** Chiavi vietate: bloccano il prototype pollution via path scritti dall'admin. */
const FORBIDDEN_KEYS = new Set(['__proto__', 'prototype', 'constructor'])

/**
 * Nomi che SEMBRANO root dello scope ma non lo sono: esistono nell'API del
 * `WizardHost` o nel contesto TS, non nel DSL. Scritti in un template
 * ricadrebbero su `item.<nome>` e risolverebbero a vuoto in silenzio — che è
 * il modo peggiore di sbagliare, perché non lascia traccia.
 */
const ROOT_FRAINTESE: Record<string, string> = {
  helpers: "`helpers` contiene le FUNZIONI per le azioni di dominio e non è leggibile dal DSL. "
    + 'Per i valori usa `env.*` (prop `env` del WizardHost) o `inputs.*` (dichiarati dal flow).',
  ctx: 'lo scope è già il contesto: usa direttamente `item.*`, `answers.*`, `vars.*`.',
  context: 'lo scope è già il contesto: usa direttamente `item.*`, `answers.*`, `vars.*`.',
  flow: 'i dati del flow non sono leggibili dal DSL. Usa `inputs.*` per i parametri in ingresso.',
}

/** Un path si valuta a ogni render: senza memoria l'avviso inonderebbe la console. */
const giaAvvisati = new Set<string>()

function avvisaSeRootFraintesa(root: string, path: string): void {
  const spiegazione = ROOT_FRAINTESE[root]
  if (!spiegazione || giaAvvisati.has(path)) return
  giaAvvisati.add(path)
  console.warn(`[flow/scope] '${path}' non risolverà niente: ${spiegazione}`)
}

export function isKnownRoot(root: string): boolean {
  return KNOWN_ROOTS.has(root)
}

/**
 * Legge un valore dallo scope.
 *
 *   'item.reparto'          → scope.item.reparto
 *   'answers.ore.previste'  → scope.answers.ore.previste
 *   'vars.statoRicezioneId' → scope.vars.statoRicezioneId
 *   '$answer.scelta'        → scope.$answer.scelta
 *   'tipoRicezioneCode'     → scope.item.tipoRicezioneCode   (fallback)
 *
 * I `ref` incontrati lungo il cammino vengono srotolati: l'host passa spesso
 * `setting` come ref di Vue.
 */
export function getScopeValue(scope: FlowScope, path: string): unknown {
  if (!path) return undefined

  const parts = path.split('.').filter((p) => p !== '')
  if (parts.length === 0) return undefined
  if (parts.some((p) => FORBIDDEN_KEYS.has(p))) {
    console.warn('[flow/scope] path rifiutato:', path)
    return undefined
  }

  // `$answer` senza suffisso = la risposta intera del nodo corrente.
  if (parts.length === 1 && parts[0] === '$answer') return unref(scope.$answer)

  const root = parts[0]
  if (!isKnownRoot(root)) avvisaSeRootFraintesa(root, path)

  const rest = isKnownRoot(root) ? parts.slice(1) : parts
  let current: unknown = isKnownRoot(root) ? scope[root] : scope.item

  for (const part of rest) {
    current = unref(current)
    if (current === null || current === undefined) return undefined
    if (typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }

  return unref(current)
}

/**
 * Scrive un valore nello scope. Consentita SOLO sotto `item.*` e `answers.*`:
 * un'azione configurata da un admin non deve poter riscrivere `setting`,
 * `user` o strutture dell'applicazione.
 */
export function setScopeValue(scope: FlowScope, path: string, value: unknown): boolean {
  const parts = path.split('.').filter((p) => p !== '')
  if (parts.length === 0) return false
  if (parts.some((p) => FORBIDDEN_KEYS.has(p))) {
    console.warn('[flow/scope] scrittura rifiutata (chiave vietata):', path)
    return false
  }

  // Path senza root nota ⇒ si intende `item.<path>`, come in lettura.
  const rooted = isKnownRoot(parts[0]) ? parts : ['item', ...parts]
  const root = rooted[0]
  // `vars` è derivato dai campi con `exportTo` (vedi `vars.ts`): viene
  // ricalcolato a ogni lettura, quindi una scrittura qui non sopravvivrebbe
  // all'istante successivo. Meglio rifiutarla dicendo dove si dichiara.
  if (root === 'vars') {
    console.warn(
      `[flow/scope] scrittura rifiutata: 'vars' è in sola lettura, si alimenta con `
      + `\`exportTo\` sul campo che deve confluirci (${path})`,
    )
    return false
  }
  if (root !== 'item' && root !== 'answers') {
    console.warn(`[flow/scope] scrittura rifiutata: solo item.* e answers.* sono scrivibili (${path})`)
    return false
  }
  if (rooted.length < 2) {
    console.warn('[flow/scope] scrittura rifiutata: manca il campo dopo la root:', path)
    return false
  }

  let container: unknown = scope[root]
  const keys = rooted.slice(1)

  for (const key of keys.slice(0, -1)) {
    container = unref(container)
    if (container === null || container === undefined || typeof container !== 'object') {
      console.warn('[flow/scope] scrittura rifiutata: percorso inesistente:', path)
      return false
    }
    const holder = container as Record<string, unknown>
    if (holder[key] === null || holder[key] === undefined) holder[key] = {}
    container = holder[key]
  }

  container = unref(container)
  if (container === null || container === undefined || typeof container !== 'object') {
    console.warn('[flow/scope] scrittura rifiutata: destinazione non è un oggetto:', path)
    return false
  }

  const target = container as Record<string, unknown>
  const last = keys[keys.length - 1]
  // Se la proprietà esistente è un ref, si scrive dentro il ref, non sopra.
  const existing = target[last]
  if (isRef(existing)) existing.value = value
  else target[last] = value

  return true
}
